import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import routes from "routes";
import { useMaterialUIController, setMiniSidenav } from "context";
import brandWhite from "assets/images/logo-logicookie.png";
import brandDark from "assets/images/logo-logicookie-dark.png";
import brandIconLight from "assets/images/logo-logicookie-sm.svg";
import brandIconDark from "assets/images/logo-logicookie-sm-dark.svg";
import SignInIllustration from "layouts/authentication/sign-in/illustration";
import ResetIllustration from "layouts/authentication/reset-password/illustration";
import { isAuthorizedToken } from "utils/auth";

// Auth kontrolü için yardımcı fonksiyon
const checkAuth = () => {
  if (typeof window === "undefined") return false;
  const token = window.sessionStorage.getItem("authToken");
  if (!token) return false;

  if (!isAuthorizedToken(token)) {
    window.sessionStorage.removeItem("authToken");
    window.sessionStorage.removeItem("userData");
    window.sessionStorage.removeItem("userRole");
    return false;
  }

  return true;
};

// Özel Route bileşeni
const ProtectedRoute = ({ element, ...rest }) => {
  const location = useLocation();
  return checkAuth() ? (
    element
  ) : (
    <Navigate to="/sign-in" state={{ from: location }} replace />
  );
};

const App = () => {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Viewport tracking for popup logic (shows popup for all widths below 1200)
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1200 : false
  );
  useEffect(() => {
    function onResize() {
      setIsNarrow(window.innerWidth < 1200);
    }
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close expanded sidenav on outside click (only when width < 1200)
  useEffect(() => {
    function handleDocClick(e) {
      if (!isNarrow) return;               // only 1000–1199
      if (miniSidenav) return;             // only when expanded (mini=false)
      const withinDrawer = e.target.closest(".MuiDrawer-paper");
      const onPopupBtn  = e.target.closest("#sidenav-popup-button");
      if (!withinDrawer && !onPopupBtn) {
        setMiniSidenav(dispatch, true);    // collapse back to mini
      }
    }
    document.addEventListener("click", handleDocClick, true);
    return () => document.removeEventListener("click", handleDocClick, true);
  }, [isNarrow, miniSidenav, dispatch]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Route oluşturma fonksiyonu (auth kontrolü eklenmiş)
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        // Auth gerektirmeyen sayfalar (login, signup vb.)
        const authPrefixes = ["/sign-in", "/login"];
        const isAuthPage = authPrefixes.some((prefix) => route.route.startsWith(prefix));
        
        return (
          <Route
            exact
            path={route.route}
            element={
              isAuthPage ? (
                route.component
              ) : (
                <ProtectedRoute element={route.component} />
              )
            }
            key={route.key}
          />
        );
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && checkAuth() && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandIcon={(transparentSidenav && !darkMode) || whiteSidenav ? brandIconDark : brandIconLight}
              brandName=""
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            {isNarrow && miniSidenav && (
              <MDBox
                sx={{
                  position: "fixed",
                  right: 16,
                  bottom: 16,
                  zIndex: (theme) => theme.zIndex.modal + 1,
                }}
              >
                <IconButton
                  id="sidenav-popup-button"
                  onClick={() => setMiniSidenav(dispatch, false)}
                  size="large"
                  sx={{
                    bgcolor: "#bababa",
                    color: "black",
                    boxShadow: 3,
                    "&:hover": { bgcolor: "#a0a0a0" },
                  }}
                >
                  <Icon>menu</Icon>
                </IconButton>
              </MDBox>
            )}
          </>
        )}
        <Routes>
          {getRoutes(routes)}
          <Route path="/authentication/sign-in/illustration" element={<Navigate to="/sign-in" replace />} />
          <Route path="/reset-password" element={<ResetIllustration />} />
          <Route path="/authentication/reset-password/" element={<Navigate to="/reset-password" replace />} />
          <Route path="/sign-in" element={<SignInIllustration />} />
          <Route path="/login" element={<Navigate to="/sign-in" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && checkAuth() && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandIcon={(transparentSidenav && !darkMode) || whiteSidenav ? brandIconDark : brandIconLight}
            brandName=""
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          {isNarrow && miniSidenav && (
            <MDBox
              sx={{
                position: "fixed",
                right: 16,
                bottom: 16,
                zIndex: (theme) => theme.zIndex.modal + 1,
              }}
            >
              <IconButton
                id="sidenav-popup-button"
                onClick={() => setMiniSidenav(dispatch, false)}
                size="large"
                sx={{
                  bgcolor: "#bababa",
                  color: "black",
                  boxShadow: 3,
                  "&:hover": { bgcolor: "#a0a0a0" },
                }}
              >
                <Icon>menu</Icon>
              </IconButton>
            </MDBox>
          )}
        </>
      )}
      <Routes>
        {getRoutes(routes)}
        <Route path="/authentication/sign-in/illustration" element={<Navigate to="/sign-in" replace />} />
        <Route path="/reset-password" element={<ResetIllustration />} />
        <Route path="/authentication/reset-password/" element={<Navigate to="/reset-password" replace />} />
        <Route path="/sign-in" element={<SignInIllustration />} />
        <Route path="/login" element={<Navigate to="/sign-in" replace />} />
        <Route path="*" element={<Navigate to={checkAuth() ? "/dashboard" : "/sign-in"} />} />
      </Routes>
    </ThemeProvider>
  );
};

export default App;
